import { formatCompletedTime } from "../intelligence/intelligenceLabels";
import {
  weatherSourceGroupLabel,
} from "../../../utils/weatherDataHealthSources";
import {
  transportSourceDetailLines,
  transportSourceGroupLabel,
} from "../../../constants/transportSourceConstants";
import {
  cultureSourceDetailLines,
  cultureSourceGroupLabel,
} from "../../../constants/cultureSourceConstants";
import {
  foodSourceDetailLines,
  foodSourceGroupLabel,
} from "../../../constants/foodSourceConstants";
import {
  buildDataSourceDetailHref,
  navigateToDataSourceDetail,
} from "../../../utils/dataSourceDetailRoutes";
import SourceHealthBadge from "./SourceHealthBadge";
import AdminTableContainer from "./layout/AdminTableContainer";

const DOMAIN_LABELS = {
  weather: "Weather",
  transport: "Transport",
  food: "Food",
  culture: "Culture",
  vehicle_market: "Vehicle Market",
  real_estate: "Real Estate",
  vendor_directory: "Vendor Directory",
  ingestion: "Ingestion",
};

const DETAIL_LINK_DOMAINS = new Set([
  "weather",
  "transport",
  "food",
  "culture",
  "vehicle_market",
  "real_estate",
  "vendor_directory",
  "finance",
  "analytics",
]);

function domainLabel(sourceDomain) {
  return DOMAIN_LABELS[sourceDomain] || sourceDomain || "—";
}

function canOpenDataSourceDetail(source) {
  const domainId = String(source?.sourceDomain || "").trim();
  const sourceKey = String(source?.sourceCode || "").trim();
  return Boolean(domainId && sourceKey && DETAIL_LINK_DOMAINS.has(domainId));
}

const DataSourceStatusTable = ({ sources }) => {
  if (!sources?.length) {
    return <p className="muted">No data sources match the current filter.</p>;
  }

  return (
    <AdminTableContainer>
      <table className="admin-data-health-table">
        <thead>
          <tr>
            <th>Source</th>
            <th>Domain</th>
            <th>Type</th>
            <th>Active</th>
            <th>Latest status</th>
            <th>Last success</th>
            <th>Received</th>
            <th>Inserted</th>
            <th>Updated</th>
            <th>Failed</th>
            <th>Health</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((source) => (
            <tr key={source.sourceCode || source.sourceId}>
              <td>
                {canOpenDataSourceDetail(source) ? (
                  <a
                    className="admin-ds-source-name-link"
                    href={buildDataSourceDetailHref(source.sourceDomain, source.sourceCode)}
                    onClick={(event) => {
                      event.preventDefault();
                      navigateToDataSourceDetail(source.sourceDomain, source.sourceCode);
                    }}
                  >
                    {source.sourceName}
                  </a>
                ) : (
                  <strong>{source.sourceName}</strong>
                )}
                <span className="admin-data-health-code">{source.sourceCode}</span>
              </td>
              <td>
                <span className={`admin-data-health-domain admin-data-health-domain--${source.sourceDomain || "ingestion"}`}>
                  {domainLabel(source.sourceDomain)}
                </span>
                {source.sourceGroup && source.sourceDomain === "weather" ? (
                  <span className="admin-data-health-code">
                    {weatherSourceGroupLabel(source.sourceGroup) || source.sourceGroup}
                  </span>
                ) : null}
                {source.sourceGroup && source.sourceDomain === "transport" ? (
                  <span className="admin-data-health-code">
                    {transportSourceGroupLabel(source.sourceGroup) || source.sourceGroup}
                  </span>
                ) : null}
                {source.sourceGroup && source.sourceDomain === "culture" ? (
                  <span className="admin-data-health-code">
                    {cultureSourceGroupLabel(source.sourceGroup) || source.sourceGroup}
                  </span>
                ) : null}
                {source.sourceGroup && source.sourceDomain === "food" ? (
                  <span className="admin-data-health-code">
                    {foodSourceGroupLabel(source.sourceGroup) || source.sourceGroup}
                  </span>
                ) : null}
              </td>
              <td>{source.sourceType}</td>
              <td>{source.isActive ? "Yes" : "No"}</td>
              <td>
                <SourceHealthBadge status={source.latestRunStatus || "never_run"} />
              </td>
              <td>{formatCompletedTime(source.lastSuccessfulRunAt)}</td>
              <td>{source.latestRecordsReceived}</td>
              <td>{source.latestRecordsInserted}</td>
              <td>{source.latestRecordsUpdated}</td>
              <td>{source.latestRecordsFailed}</td>
              <td>
                <SourceHealthBadge status={source.healthStatus} />
                {source.activationNote ? (
                  <span className="admin-data-health-code">{source.activationNote}</span>
                ) : null}
                {source.sourceDomain === "transport"
                  ? transportSourceDetailLines(source).map((line) => (
                      <span className="admin-data-health-code" key={line}>
                        {line}
                      </span>
                    ))
                  : null}
                {source.sourceDomain === "culture"
                  ? cultureSourceDetailLines(source).map((line) => (
                      <span className="admin-data-health-code" key={line}>
                        {line}
                      </span>
                    ))
                  : null}
                {source.sourceDomain === "food"
                  ? foodSourceDetailLines(source).map((line) => (
                      <span className="admin-data-health-code" key={line}>
                        {line}
                      </span>
                    ))
                  : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </AdminTableContainer>
  );
};

export default DataSourceStatusTable;
